import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Eye, MousePointerClick, FileText, TrendingUp, Loader2, Users, ArrowUpRight,
  BarChart3, Globe, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/lib/api";

function MetricCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
    </div>
  );
}

function MiniBar({ value, max }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-24 h-2 rounded-full bg-gray-800 overflow-hidden">
      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }}></div>
    </div>
  );
}

function DailyChart({ daily }) {
  const days = Object.keys(daily).sort().slice(-14);
  if (days.length === 0) return <p className="text-gray-500 text-sm text-center py-8">Sin datos todavía</p>;

  const maxViews = Math.max(...days.map(d => daily[d]?.page_view || 0), 1);

  return (
    <div className="flex items-end gap-1.5 h-40" data-testid="daily-chart">
      {days.map(day => {
        const views = daily[day]?.page_view || 0;
        const conversions = (daily[day]?.form_submit_contacto || 0) + (daily[day]?.form_submit_diagnostico || 0) + (daily[day]?.form_submit_lead || 0);
        const h = Math.max((views / maxViews) * 100, 4);
        const label = day.slice(5);
        return (
          <div key={day} className="flex-1 flex flex-col items-center gap-1 group" title={`${day}: ${views} visitas, ${conversions} conversiones`}>
            <span className="text-[10px] text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">{views}</span>
            <div className="w-full flex flex-col items-center gap-0.5">
              <div className="w-full rounded-t bg-blue-500/80 transition-all" style={{ height: `${h}%`, minHeight: '3px' }}></div>
              {conversions > 0 && <div className="w-full rounded-b bg-green-500" style={{ height: `${Math.max((conversions / maxViews) * 100, 3)}%`, minHeight: '3px' }}></div>}
            </div>
            <span className="text-[9px] text-gray-600 -rotate-45 origin-center whitespace-nowrap">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function FunnelStep({ label, value, pct, color }) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-gray-300 text-sm">{label}</span>
          <span className="text-white font-bold">{value}</span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-gray-800 overflow-hidden">
          <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }}></div>
        </div>
      </div>
      <span className="text-gray-500 text-xs w-12 text-right">{pct.toFixed(1)}%</span>
    </div>
  );
}

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [summary, setSummary] = useState(null);
  const [conversions, setConversions] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setUser(res.data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/conversions?days=${days}`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setConversions(c.data);
    } catch (err) {
      toast.error("Error al cargar analytics");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { if (user) fetchData(); }, [user, days]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Analytics</h2>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 bg-[#1A1F2E] border-gray-700 text-white" data-testid="analytics-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="ghost" size="sm" className="text-gray-400 hover:text-white" disabled={refreshing} data-testid="analytics-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {(!summary || !conversions) ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-metrics">
            <MetricCard icon={Eye} label="Visitas" value={conversions.views} color="bg-blue-500/20" />
            <MetricCard icon={MousePointerClick} label="Clics CTA" value={conversions.cta_clicks} sub={`${conversions.cta_rate}% de visitas`} color="bg-purple-500/20" />
            <MetricCard icon={FileText} label="Conversiones" value={conversions.total_conversions} sub={`${conversions.conversion_rate}% tasa`} color="bg-green-500/20" />
            <MetricCard icon={TrendingUp} label="Tasa conversión" value={`${conversions.conversion_rate}%`} color="bg-yellow-500/20" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Daily chart */}
            <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid="daily-views-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold text-sm">Visitas diarias</h3>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-blue-500"></span> Visitas</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded bg-green-500"></span> Conversiones</span>
                </div>
              </div>
              <DailyChart daily={summary.daily} />
            </div>

            {/* Conversion funnel */}
            <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid="funnel-card">
              <h3 className="text-white font-semibold text-sm mb-5">Embudo de conversión</h3>
              <div className="space-y-4">
                <FunnelStep label="Visitas" value={conversions.views} pct={100} color="bg-blue-500" />
                <FunnelStep label="Clics en CTA" value={conversions.cta_clicks} pct={conversions.cta_rate} color="bg-purple-500" />
                <FunnelStep label="Contacto rápido" value={conversions.form_contacto} pct={conversions.views > 0 ? (conversions.form_contacto / conversions.views * 100) : 0} color="bg-green-500" />
                <FunnelStep label="Diagnóstico completo" value={conversions.form_diagnostico} pct={conversions.views > 0 ? (conversions.form_diagnostico / conversions.views * 100) : 0} color="bg-emerald-500" />
                <FunnelStep label="Lead checklist" value={conversions.form_lead} pct={conversions.views > 0 ? (conversions.form_lead / conversions.views * 100) : 0} color="bg-teal-500" />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top pages */}
            <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid="top-pages-card">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-400" /> Páginas más visitadas</h3>
              {summary.top_pages.length === 0 ? (
                <p className="text-gray-500 text-sm">Sin datos</p>
              ) : (
                <div className="space-y-3">
                  {summary.top_pages.slice(0, 8).map((p, i) => (
                    <div key={p.page} className="flex items-center gap-3">
                      <span className="text-gray-600 text-xs w-5">{i + 1}</span>
                      <span className="text-gray-300 text-sm flex-1 truncate">{p.page || "/"}</span>
                      <MiniBar value={p.count} max={summary.top_pages[0]?.count || 1} />
                      <span className="text-white text-sm font-medium w-10 text-right">{p.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top referrers */}
            <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-xl p-5" data-testid="top-referrers-card">
              <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-400" /> Fuentes de tráfico</h3>
              {summary.top_referrers.length === 0 ? (
                <p className="text-gray-500 text-sm">Sin datos de referrers</p>
              ) : (
                <div className="space-y-3">
                  {summary.top_referrers.slice(0, 8).map((r, i) => {
                    let domain = r.referrer;
                    try { domain = new URL(r.referrer).hostname; } catch (e) { /* keep original */ }
                    return (
                      <div key={r.referrer} className="flex items-center gap-3">
                        <span className="text-gray-600 text-xs w-5">{i + 1}</span>
                        <span className="text-gray-300 text-sm flex-1 truncate">{domain}</span>
                        <MiniBar value={r.count} max={summary.top_referrers[0]?.count || 1} />
                        <span className="text-white text-sm font-medium w-10 text-right">{r.count}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
